import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Plus, RefreshCw, Search, Ban, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BulkBar, RowCheck } from "./BulkBar";

export function AdminPassesTab({ buildings = [] }) {
  const [passes, setPasses] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [buildingId, setBuildingId] = useState("");

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ resident_id: "", visitor_name: "", valid_hours: 24, purpose: "" });
  const [saving, setSaving] = useState(false);

  const [toRevoke, setToRevoke] = useState(null);

  // --- bulk selection ----------------------------------------------------
  const [selected, setSelected] = useState(() => new Set());
  const [bulkRevoke, setBulkRevoke] = useState(false);
  const [bulkDelete, setBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (buildingId) params.building_id = buildingId;
      const [p, r] = await Promise.all([
        api.get("/platform/passes", { params }),
        api.get("/platform/users", { params: { role: "resident" } }),
      ]);
      setPasses(p.data);
      setResidents(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load passes");
    } finally { setLoading(false); }
  }, [q, status, buildingId]);
  useEffect(() => { load(); }, [load]);

  function openNew() {
    setForm({ resident_id: "", visitor_name: "", valid_hours: 24, purpose: "" });
    setCreating(true);
  }

  async function save() {
    setSaving(true);
    try {
      await api.post("/platform/passes", {
        resident_id: form.resident_id,
        visitor_name: form.visitor_name,
        valid_hours: Number(form.valid_hours) || 24,
        purpose: form.purpose || null,
      });
      toast.success("Pass issued");
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  }

  async function doRevoke() {
    try {
      await api.post(`/platform/passes/${toRevoke.id}/revoke`);
      toast.success("Pass revoked");
      setToRevoke(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  }

  // --- bulk helpers ------------------------------------------------------
  function toggleRow(id, on) {
    setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  }
  function toggleAllOnPage(on) {
    setSelected(() => (on ? new Set(passes.map((p) => p.id)) : new Set()));
  }
  function clearSelection() { setSelected(new Set()); }

  async function runBulkRevoke() {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/bulk/passes/revoke", { ids: Array.from(selected) });
      const skippedN = data.skipped?.length || 0;
      const skippedWhy = (data.skipped || []).slice(0, 2).map((s) => s.reason).join("; ");
      toast.success(`${data.revoked} revoked${skippedN ? ` · ${skippedN} skipped${skippedWhy ? " ("+skippedWhy+")" : ""}` : ""}`);
      setBulkRevoke(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk revoke failed");
    } finally { setBulkBusy(false); }
  }

  async function runBulkDelete() {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/bulk/passes/delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} deleted`);
      setBulkDelete(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  }

  const allOnPageChecked = passes.length > 0 && passes.every((p) => selected.has(p.id));

  const buildingCodeById = (id) => buildings.find((b) => b.id === id)?.code || "—";
  const filteredResidents = residents.filter((r) => !buildingId || r.building_id === buildingId);

  return (
    <div className="space-y-4" data-testid="admin-passes-tab">
      <div className="card-stroke p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="label-tiny">Search</Label>
          <div className="relative mt-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="visitor name or code"
                   className="pl-9" data-testid="passes-filter-q"/>
          </div>
        </div>
        <div className="w-40">
          <Label className="label-tiny">Status</Label>
          <Select value={status || "all"} onValueChange={(v)=>setStatus(v==="all"?"":v)}>
            <SelectTrigger className="mt-2" data-testid="passes-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="used">Used</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <Label className="label-tiny">Building</Label>
          <Select value={buildingId || "all"} onValueChange={(v)=>setBuildingId(v==="all"?"":v)}>
            <SelectTrigger className="mt-2" data-testid="passes-filter-building"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All buildings</SelectItem>
              {buildings.map((b)=>(<SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} data-testid="passes-refresh">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
        </Button>
        <Button onClick={openNew} data-testid="passes-new">
          <Plus size={14} className="mr-1.5"/> Issue pass
        </Button>
      </div>

      <div className="card-stroke p-0 overflow-x-auto">
        <BulkBar count={selected.size} onClear={clearSelection} testId="passes-bulk-bar">
          <Button size="sm" variant="outline" onClick={() => setBulkRevoke(true)}
                  data-testid="passes-bulk-revoke">
            <Ban size={14} className="mr-1.5 text-destructive"/> Revoke
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBulkDelete(true)}
                  className="text-destructive hover:text-destructive"
                  data-testid="passes-bulk-delete">
            <Trash2 size={14} className="mr-1.5"/> Delete
          </Button>
        </BulkBar>
        <table className="w-full text-sm" data-testid="passes-table">
          <thead>
            <tr className="label-tiny text-left border-b border-border">
              <th className="py-3 pl-4 pr-2 w-10">
                <RowCheck checked={allOnPageChecked} onChange={toggleAllOnPage}
                          testId="passes-select-all" label="Select all passes"/>
              </th>
              <th>Code</th><th>Visitor</th><th>Building</th>
              <th>Status</th><th>Valid until</th><th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {passes.length === 0 ? (
              <tr><td colSpan={7} className="py-8 text-center text-muted-foreground" data-testid="passes-empty">
                {loading ? "Loading…" : "No passes found."}
              </td></tr>
            ) : passes.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0" data-testid={`pass-row-${p.id}`}>
                <td className="py-3 pl-4 pr-2">
                  <RowCheck checked={selected.has(p.id)} onChange={(on)=>toggleRow(p.id, on)}
                            testId={`pass-select-${p.id}`}/>
                </td>
                <td className="font-mono text-xs">{p.code}</td>
                <td>{p.visitor_name}</td>
                <td className="font-mono text-xs">{buildingCodeById(p.building_id)}</td>
                <td>
                  <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full ${
                    p.status === "active" ? "bg-emerald-500/15 text-emerald-600"
                    : p.status === "revoked" ? "bg-destructive/15 text-destructive"
                    : "bg-secondary text-secondary-foreground"
                  }`}>{p.status}</span>
                </td>
                <td className="font-mono text-xs text-muted-foreground">
                  {p.valid_until ? new Date(p.valid_until).toLocaleString() : "—"}
                </td>
                <td className="text-right pr-4 whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={()=>setToRevoke(p)}
                          disabled={p.status !== "active"}
                          data-testid={`pass-revoke-${p.id}`}>
                    <Ban size={14} className="mr-1.5 text-destructive"/> Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="pass-modal">
          <DialogHeader>
            <DialogTitle>Issue a pass on behalf of a resident</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-tiny">Resident</Label>
              <Select value={form.resident_id} onValueChange={(v)=>setForm({...form, resident_id: v})}>
                <SelectTrigger className="mt-2" data-testid="pass-form-resident"><SelectValue placeholder="Pick a resident"/></SelectTrigger>
                <SelectContent>
                  {filteredResidents.map((r)=>(
                    <SelectItem key={r.id} value={r.id}>
                      {r.name} · {r.email} {r.building_code ? `· ${r.building_code}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-tiny">Visitor name</Label>
              <Input value={form.visitor_name} onChange={(e)=>setForm({...form, visitor_name: e.target.value})} className="mt-2" data-testid="pass-form-visitor"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-tiny">Valid for (hours)</Label>
                <Input type="number" min="1" max="720" value={form.valid_hours}
                       onChange={(e)=>setForm({...form, valid_hours: e.target.value})}
                       className="mt-2 font-mono" data-testid="pass-form-hours"/></div>
              <div><Label className="label-tiny">Purpose (optional)</Label>
                <Input value={form.purpose} onChange={(e)=>setForm({...form, purpose: e.target.value})}
                       className="mt-2" placeholder="delivery / guest / …" data-testid="pass-form-purpose"/></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreating(false)} data-testid="pass-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving || !form.resident_id || !form.visitor_name}
                    data-testid="pass-form-save">
              {saving ? "Issuing…" : "Issue pass"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toRevoke} onOpenChange={(o)=>{ if (!o) setToRevoke(null); }}>
        <DialogContent data-testid="pass-revoke-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Revoke pass {toRevoke?.code}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            The guard will no longer be able to admit visitor <span className="font-medium text-foreground">{toRevoke?.visitor_name}</span> with this code.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setToRevoke(null)} data-testid="pass-revoke-cancel">Cancel</Button>
            <Button onClick={doRevoke} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="pass-revoke-confirm">
              Revoke pass
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkRevoke} onOpenChange={setBulkRevoke}>
        <DialogContent data-testid="passes-bulk-revoke-dialog">
          <DialogHeader>
            <DialogTitle>Revoke {selected.size} pass{selected.size === 1 ? "" : "es"}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Passes that are already used, revoked or expired will be skipped. This cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkRevoke(false)} data-testid="passes-bulk-revoke-cancel">Cancel</Button>
            <Button onClick={runBulkRevoke} disabled={bulkBusy}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="passes-bulk-revoke-confirm">
              {bulkBusy ? "Revoking…" : `Revoke ${selected.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDelete} onOpenChange={setBulkDelete}>
        <DialogContent data-testid="passes-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete {selected.size} pass{selected.size === 1 ? "" : "es"}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Pass records (not just the status) will be removed. This cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkDelete(false)} data-testid="passes-bulk-delete-cancel">Cancel</Button>
            <Button onClick={runBulkDelete} disabled={bulkBusy}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="passes-bulk-delete-confirm">
              {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
